import type { CmpConfigPayload, GvlVendor } from "./types";
import { NO_LI_PURPOSE_IDS } from "./types";

/**
 * Effective TCF legal bases per vendor, after publisher purpose selection
 * (config.gvl.purposes is already trimmed server-side) and Publisher Restrictions.
 * Mirrors app/services/tcf_disclosure.rb — keep both in sync.
 */
export type VendorBasis = {
  id: number;
  consent: number[];
  legInt: number[];
  specialPurposes: number[];
  features: number[];
  specialFeatures: number[];
};

export type Disclosure = {
  vendors: Map<number, VendorBasis>;
  /** purpose id -> vendor ids asking for consent */
  consentPurposeVendors: Map<number, number[]>;
  /** purpose id -> vendor ids relying on legitimate interest */
  liPurposeVendors: Map<number, number[]>;
  specialPurposeVendors: Map<number, number[]>;
  featureVendors: Map<number, number[]>;
  specialFeatureVendors: Map<number, number[]>;
  consentVendorIds: number[];
  liVendorIds: number[];
};

type Restriction = "not_allowed" | "require_consent" | "require_li";

const RESTRICTION_TYPES: Record<string, Restriction> = {
  "0": "not_allowed", not_allowed: "not_allowed",
  "1": "require_consent", require_consent: "require_consent", consent: "require_consent",
  "2": "require_li", require_li: "require_li", li: "require_li"
};

export function vendorBasis(
  v: GvlVendor,
  restrictions: Map<string, Restriction>,
  disclosedPurposes: Set<number>,
  disclosedSpecialFeatures: Set<number>
): VendorBasis {
  const consent: number[] = [];
  const legInt: number[] = [];
  const declaredConsent = new Set(v.purposes || []);
  const flexible = new Set(v.flexiblePurposes || []);
  const candidates = Array.from(new Set([...(v.purposes || []), ...(v.legIntPurposes || [])]));
  for (const pid of candidates) {
    if (!disclosedPurposes.has(pid)) continue;
    const declared = declaredConsent.has(pid) ? "consent" : "li";
    let basis: "consent" | "li" | null = declared;
    switch (restrictions.get(`${v.id}:${pid}`)) {
      case "not_allowed": basis = null; break;
      case "require_consent": basis = declared === "consent" || flexible.has(pid) ? "consent" : null; break;
      case "require_li": basis = declared === "li" || flexible.has(pid) ? "li" : null; break;
    }
    if (basis === "li" && NO_LI_PURPOSE_IDS.has(pid)) basis = null;
    if (basis === "consent") consent.push(pid);
    if (basis === "li") legInt.push(pid);
  }
  return {
    id: v.id,
    consent: consent.sort((a, b) => a - b),
    legInt: legInt.sort((a, b) => a - b),
    specialPurposes: [...(v.specialPurposes || [])].sort((a, b) => a - b),
    features: [...(v.features || [])].sort((a, b) => a - b),
    specialFeatures: (v.specialFeatures || []).filter((id) => disclosedSpecialFeatures.has(id)).sort((a, b) => a - b)
  };
}

const cache = new WeakMap<CmpConfigPayload, Disclosure>();

export function computeDisclosure(config: CmpConfigPayload): Disclosure {
  const hit = cache.get(config);
  if (hit) return hit;

  const restrictions = new Map<string, Restriction>();
  for (const row of config.publisherRestrictions || []) {
    const type = RESTRICTION_TYPES[String(row.restriction_type).toLowerCase()];
    if (type) restrictions.set(`${Number(row.vendor_id)}:${Number(row.purpose_id)}`, type);
  }
  const disclosedPurposes = new Set((config.gvl.purposes || []).map((p) => p.id));
  const disclosedSf = new Set((config.gvl.specialFeatures || []).map((f) => f.id));

  const vendors = new Map<number, VendorBasis>();
  const push = (map: Map<number, number[]>, key: number, id: number) => {
    const list = map.get(key) || [];
    list.push(id);
    map.set(key, list);
  };
  const consentPurposeVendors = new Map<number, number[]>();
  const liPurposeVendors = new Map<number, number[]>();
  const specialPurposeVendors = new Map<number, number[]>();
  const featureVendors = new Map<number, number[]>();
  const specialFeatureVendors = new Map<number, number[]>();

  for (const v of config.gvl.vendors || []) {
    const b = vendorBasis(v, restrictions, disclosedPurposes, disclosedSf);
    vendors.set(v.id, b);
    b.consent.forEach((pid) => push(consentPurposeVendors, pid, v.id));
    b.legInt.forEach((pid) => push(liPurposeVendors, pid, v.id));
    b.specialPurposes.forEach((id) => push(specialPurposeVendors, id, v.id));
    b.features.forEach((id) => push(featureVendors, id, v.id));
    b.specialFeatures.forEach((id) => push(specialFeatureVendors, id, v.id));
  }

  const result: Disclosure = {
    vendors,
    consentPurposeVendors,
    liPurposeVendors,
    specialPurposeVendors,
    featureVendors,
    specialFeatureVendors,
    consentVendorIds: [...vendors.values()].filter((b) => b.consent.length > 0).map((b) => b.id),
    liVendorIds: [...vendors.values()].filter((b) => b.legInt.length > 0).map((b) => b.id)
  };
  cache.set(config, result);
  return result;
}
